/**
 * Helpers puros de "Leads y consultas" de un registro (R1, `getrecordleads`).
 * Ver docs/maps/leads.md § Leads y consultas de una unidad.
 */

export const RECORD_LEADS_TITLE = 'Leads y consultas';
/** Header colapsado sin consultas ni leads. */
export const RECORD_LEADS_EMPTY_SUMMARY = 'Sin actividad';

/**
 * Via en ficha de registro: el interés del registro ya se sobreentiende, así
 * que `inquiry` / `both` dicen solo “Consulta”. `interest` (sin consulta) sigue
 * como “Interés”.
 */
const VIA_LABELS = {
  interest: 'Interés',
  inquiry: 'Consulta',
  both: 'Consulta',
};

/** Etiqueta de `leads[].via` (`interest` | `inquiry` | `both`). Desconocido → ''. */
export function getRecordLeadViaLabel(via) {
  return VIA_LABELS[String(via || '').trim()] || '';
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/**
 * Resumen corto desde `stats`: "3 consultas · 2 leads". Sin nada → ''.
 * Las no leídas van en el badge, no en el texto.
 *
 * @param {{ inquiryCount?: number, leadCount?: number }} stats
 * @returns {string}
 */
export function describeRecordLeadsStats(stats) {
  const inquiries = Number(stats?.inquiryCount) || 0;
  const leads = Number(stats?.leadCount) || 0;
  return [
    inquiries ? plural(inquiries, 'consulta', 'consultas') : '',
    leads ? plural(leads, 'lead', 'leads') : '',
  ].filter(Boolean).join(' · ');
}

/** Meta de una fila de lead: "2 consultas · 1 sin leer" (de este registro). */
export function describeRecordLeadCounts(lead) {
  const inquiries = Number(lead?.inquiry_count) || 0;
  const unseen = Number(lead?.unseen_inquiry_count) || 0;
  return [
    inquiries ? plural(inquiries, 'consulta', 'consultas') : '',
    unseen ? `${unseen} sin leer` : '',
  ].filter(Boolean).join(' · ');
}

/** Aria / tooltip del badge de no leídas. */
export function describeRecordUnseen(count) {
  const n = Number(count) || 0;
  if (n <= 0) return '';
  return n === 1 ? '1 consulta sin leer' : `${n} consultas sin leer`;
}

/**
 * Tras marcar vista una consulta: baja `stats.unseenCount` y el
 * `unseen_inquiry_count` del lead vinculado (solo cuenta las de este registro).
 */
export function applyRecordInquirySeen(state, inquiry, updated = null) {
  const id = String(inquiry?.id || '');
  if (!state || !id) return state;
  const seenAt = updated?.seen_at || new Date().toISOString();
  const leadId = String(inquiry?.lead_id || '');
  return {
    ...state,
    inquiries: state.inquiries.map((row) => (row.id === id ? { ...row, ...(updated || {}), seen_at: seenAt } : row)),
    leads: leadId
      ? state.leads.map((lead) => (lead.id === leadId
        ? { ...lead, unseen_inquiry_count: Math.max(0, (Number(lead.unseen_inquiry_count) || 0) - 1) }
        : lead))
      : state.leads,
    stats: { ...state.stats, unseenCount: Math.max(0, state.stats.unseenCount - 1) },
  };
}
